import { useCallback, useEffect, useRef } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

// ── Constants ────────────────────────────────────────────────────────────────
const DRAG_THRESHOLD = 5; // pixels — distinguishes a "click" from a camera drag

// ── Types ────────────────────────────────────────────────────────────────────
type MeasurementIntersection = {
  point: THREE.Vector3;
  isLamp: boolean;
  lampId?: string;
};

type MeasurementActions = {
  addPoint: (v: THREE.Vector3) => void;
  clearPoints: () => void;
  setHoverPoint: (v: THREE.Vector3 | null) => void;
  setIsHoverSnapped: (v: boolean) => void;
  addMeasurementPoint: (p: [number, number, number]) => void;
  clearMeasurementPoints: () => void;
};

type HoverCallback = (layerName: string | null) => void;

type UseSceneInteractionOptions = {
  /** Is the measurement tool currently active? */
  isMeasurementActive: boolean;
  /** Is lamp-creation mode active? */
  isLampCreationMode: boolean;
  /** Is lamp-distance measurement mode active? */
  isLampDistanceMode: boolean;
  /** Local measurement points array */
  measurementPoints: THREE.Vector3[];
  /** Current hover point (to clear when needed) */
  localHoverPoint: THREE.Vector3 | null;
  /** Callbacks for measurement state updates */
  measurementActions: MeasurementActions;
  /** Layer hover callback */
  setHoveredLayer: HoverCallback;
  /** Deselect all lamps */
  selectLamp: (id: string) => void;
  /** Intersection helper for measurement mode */
  getIntersectionPoint: (mouse: THREE.Vector2) => MeasurementIntersection | null;
  /** Luminaires are not shown (e.g. real-time render) — skip lamp quick-edit on right-click */
  isLampQuickEditDisabled?: boolean;
  /** Fully disable layer/lamp hover + select + quick-edit (e.g. while drawing a measurement surface). */
  disabled?: boolean;
};

/**
 * useSceneInteraction
 *
 * Centralises all pointer/click handling for the 3D scene canvas.
 * Implements a strict click hierarchy:
 *
 *   1. Lamp click     → already handled by R3F (marked on native event) → skip
 *   2. Camera drag    → pointer travelled > threshold → skip
 *   3. Measurement    → active measurement tool consumes the click
 *   4. Layer / Normal → raycast to find the closest interactive object
 *       4a. Hit is a lamp  → skip (R3F handles it)
 *       4b. Hit is a layer → select it + dispatch quick-edit popup
 *       4c. Miss            → deselect
 *
 * Hover: updates hovered layer via throttled RAF raycasting.
 */
export function useSceneInteraction({
  isMeasurementActive,
  isLampCreationMode,
  isLampDistanceMode,
  measurementPoints,
  localHoverPoint,
  measurementActions,
  setHoveredLayer,
  selectLamp,
  getIntersectionPoint,
  isLampQuickEditDisabled = false,
  disabled = false,
}: UseSceneInteractionOptions) {
  const { camera, raycaster, scene, gl } = useThree();

  // ── Reusable mouse vector ──────────────────────────────────────────────────
  const mouseVec = useRef(new THREE.Vector2());

  // ── Drag tracking (refs survive effect re-runs) ────────────────────────────
  const pointerDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const pointerTravelRef = useRef(0);

  // ── Hover throttle ─────────────────────────────────────────────────────────
  const hoverRafId = useRef(0);
  const pendingHoverEvent = useRef<PointerEvent | null>(null);

  // ── Stable callback refs (prevent effect churn on every render) ───────────
  const setHoveredLayerRef = useRef(setHoveredLayer);
  setHoveredLayerRef.current = setHoveredLayer;
  const selectLampRef = useRef(selectLamp);
  selectLampRef.current = selectLamp;
  const getIntersectionPointRef = useRef(getIntersectionPoint);
  getIntersectionPointRef.current = getIntersectionPoint;
  const measurementActionsRef = useRef(measurementActions);
  measurementActionsRef.current = measurementActions;
  const isMeasurementActiveRef = useRef(isMeasurementActive);
  isMeasurementActiveRef.current = isMeasurementActive;
  const isLampCreationModeRef = useRef(isLampCreationMode);
  isLampCreationModeRef.current = isLampCreationMode;
  const isLampDistanceModeRef = useRef(isLampDistanceMode);
  isLampDistanceModeRef.current = isLampDistanceMode;
  const measurementPointsRef = useRef(measurementPoints);
  measurementPointsRef.current = measurementPoints;
  const localHoverPointRef = useRef(localHoverPoint);
  localHoverPointRef.current = localHoverPoint;
  const isLampQuickEditDisabledRef = useRef(isLampQuickEditDisabled);
  isLampQuickEditDisabledRef.current = isLampQuickEditDisabled;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // Clear any lingering layer highlight the moment interaction is disabled.
  useEffect(() => {
    if (disabled) setHoveredLayerRef.current(null);
  }, [disabled]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Canvas-relative NDC from a pointer/mouse event. */
  const calculateNDC = useCallback(
    (event: MouseEvent | PointerEvent): THREE.Vector2 => {
      const rect = gl.domElement.getBoundingClientRect();
      mouseVec.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseVec.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      return mouseVec.current;
    },
    [gl.domElement],
  );

  /** Collect interactive scene objects (layers + lamps) in a single traverse. Pass
   *  `includeMeasurementPoints` to also gather measurement points (they can "block"
   *  hover), so the caller doesn't need a second full-scene traverse. */
  const getInteractiveObjects = useCallback(
    (includeMeasurementPoints = false): THREE.Object3D[] => {
      const objects: THREE.Object3D[] = [];
      scene.traverse((obj) => {
        if (!(obj as THREE.Mesh).isMesh || !obj.visible) return;
        if (obj.userData.layerName || obj.userData.type === 'lamp' || obj.userData.onClick) {
          objects.push(obj);
        } else if (includeMeasurementPoints && obj.userData.isMeasurementPoint) {
          objects.push(obj);
        }
      });
      return objects;
    },
    [scene],
  );

  // ── Event handlers (single useEffect — refs keep it stable across renders) ──
  useEffect(() => {
    const canvas = gl.domElement;

    // ────────────────────────────────────────────────────────────────────────
    // HOVER
    // ────────────────────────────────────────────────────────────────────────
    const processHover = () => {
      hoverRafId.current = 0;
      const event = pendingHoverEvent.current;
      if (!event) return;
      pendingHoverEvent.current = null;

      // Interaction disabled (e.g. drawing a measurement surface) — no hover highlight.
      if (disabledRef.current) {
        setHoveredLayerRef.current(null);
        return;
      }

      const mouse = calculateNDC(event);

      // Rubber-band while drawing a measurement (show preview from last point)
      if (isMeasurementActiveRef.current && measurementPointsRef.current.length > 0) {
        const intersection = getIntersectionPointRef.current(mouse);
        if (intersection) {
          measurementActionsRef.current.setHoverPoint(intersection.point.clone());
          measurementActionsRef.current.setIsHoverSnapped(intersection.isLamp);
        }
      } else if (localHoverPointRef.current) {
        measurementActionsRef.current.setHoverPoint(null);
        measurementActionsRef.current.setIsHoverSnapped(false);
      }

      // Layer / lamp hover highlight. Include measurement points so they can "block" the
      // hover: if a measurement point is the closest thing under the cursor, don't hover
      // (or select) the layer behind it — the point owns the interaction + its own popup.
      camera.updateMatrixWorld();
      raycaster.setFromCamera(mouse, camera);
      const hoverTargets = getInteractiveObjects(true);
      const hits = raycaster.intersectObjects(hoverTargets, false);
      if (hits[0]?.object.userData.isMeasurementPoint) {
        setHoveredLayerRef.current(null);
        return;
      }
      const interactive = hits.find((h) => h.object.userData.layerName || h.object.userData.type === 'lamp');
      setHoveredLayerRef.current(
        interactive ? interactive.object.userData.layerName || interactive.object.userData.name || null : null,
      );
    };

    const onPointerMove = (event: PointerEvent) => {
      // Accumulate drag distance
      if (pointerDownPosRef.current) {
        const dx = event.clientX - pointerDownPosRef.current.x;
        const dy = event.clientY - pointerDownPosRef.current.y;
        pointerTravelRef.current = Math.sqrt(dx * dx + dy * dy);
      }

      // Throttle hover to RAF
      pendingHoverEvent.current = event;
      if (!hoverRafId.current) {
        hoverRafId.current = requestAnimationFrame(processHover);
      }
    };

    /** Walk up the parent chain to find the lamp group an object belongs to. */
    const findLampAncestor = (obj: THREE.Object3D): THREE.Object3D | null => {
      let cur: THREE.Object3D | null = obj;
      while (cur) {
        if (cur.userData.type === 'lamp') return cur;
        cur = cur.parent;
      }
      return null;
    };
    const isLampDescendant = (obj: THREE.Object3D): boolean => findLampAncestor(obj) !== null;

    // ────────────────────────────────────────────────────────────────────────
    // POINTER DOWN — record start position for drag detection
    // ────────────────────────────────────────────────────────────────────────
    const onPointerDown = (event: PointerEvent) => {
      pointerDownPosRef.current = { x: event.clientX, y: event.clientY };
      pointerTravelRef.current = 0;
    };

    // ────────────────────────────────────────────────────────────────────────
    // CLICK — the main hierarchy
    // ────────────────────────────────────────────────────────────────────────
    const onClick = (event: MouseEvent) => {
      // Interaction disabled (e.g. drawing a measurement surface) — the surface editor
      // owns the canvas clicks; never select/deselect layers or lamps.
      if (disabledRef.current) return;

      // ── Step 1: Lamp already handled this event via R3F? ──
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((event as any).__lampHandled) return;

      // ── Step 2: Was this a camera drag, not a click? ──
      const wasDrag = pointerTravelRef.current > DRAG_THRESHOLD;
      pointerDownPosRef.current = null;
      pointerTravelRef.current = 0;
      if (wasDrag) return;

      // ── Step 3: Measurement mode consumes the click ──
      if (isMeasurementActiveRef.current) {
        handleMeasurementClick(event);
        return;
      }

      // ── Step 4: Normal mode — raycast for layers / lamps ──
      handleNormalClick(event);
    };

    // ── Step 3 detail: measurement click logic ──────────────────────────────
    const handleMeasurementClick = (event: MouseEvent) => {
      const mouse = calculateNDC(event);
      const intersection = getIntersectionPointRef.current(mouse);
      if (!intersection) return;

      const pts = measurementPointsRef.current;
      const { addPoint, setHoverPoint, addMeasurementPoint } = measurementActionsRef.current;

      // All measurement clicks require Ctrl/Cmd held down
      if (!event.ctrlKey && !event.metaKey) return;

      const pt = intersection.point.clone();
      addPoint(pt);
      setHoverPoint(null);

      // Persist to Redux: if this is the 2nd point, push both; otherwise just the new one
      if (pts.length === 1) {
        addMeasurementPoint([pts[0].x, pts[0].y, pts[0].z]);
      }
      if (pts.length >= 1) {
        addMeasurementPoint([pt.x, pt.y, pt.z]);
      }
    };

    // ── Step 4 detail: normal (non-measurement) click logic ─────────────────

    const handleNormalClick = (event: MouseEvent) => {
      if (!isLampCreationModeRef.current) {
        selectLampRef.current('');
      }

      camera.updateMatrixWorld();
      const mouse = calculateNDC(event);
      raycaster.setFromCamera(mouse, camera);

      // Raycast ALL visible meshes so we can detect lamps in front of layers
      const allVisible: THREE.Object3D[] = [];
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh && obj.visible) {
          allVisible.push(obj);
        }
      });

      const hits = raycaster.intersectObjects(allVisible, false);
      if (hits.length === 0) return;

      // Find the closest hit that is either a lamp or a layer
      for (const hit of hits) {
        // If the closest relevant hit is a lamp descendant → R3F handles it, stop
        if (isLampDescendant(hit.object)) return;

        const { userData } = hit.object;

        // A direct hit on a measurement point sphere is consumed by the point's
        // own click handler (popup). Don't fall through to the layer behind it.
        if (userData.isMeasurementPoint) return;

        // If it's a layer → select only (popup is on right-click), then stop
        if (userData.layerName || userData.onClick) {
          if (userData.onClick) userData.onClick();
          return;
        }
      }
    };

    // ────────────────────────────────────────────────────────────────────────
    // RIGHT-CLICK — open layer quick-edit / results popup on pointerup
    // We block contextmenu entirely and use pointerup so we can detect
    // whether the user dragged (camera orbit) before releasing.
    // ────────────────────────────────────────────────────────────────────────
    const onContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const onPointerUp = (event: PointerEvent) => {
      // Only handle right mouse button
      if (event.button !== 2) return;

      // Interaction disabled (e.g. drawing a measurement surface) — no quick-edit popup.
      if (disabledRef.current) return;

      // If the user dragged (camera orbit), skip
      if (pointerTravelRef.current > DRAG_THRESHOLD) return;

      camera.updateMatrixWorld();
      const mouse = calculateNDC(event);
      raycaster.setFromCamera(mouse, camera);

      // Single raycast that decides between lamp and layer based on closest
      // hit, then dispatches at most one popup event. The Lamp's R3F
      // onContextMenu is intentionally NOT used so we cannot end up with two
      // popups when a lamp and a layer are both under the cursor.
      const allVisible: THREE.Object3D[] = [];
      scene.traverse((obj) => {
        if ((obj as THREE.Mesh).isMesh && obj.visible) {
          allVisible.push(obj);
        }
      });
      const hits = raycaster.intersectObjects(allVisible, false);
      for (const hit of hits) {
        const lampGroup = findLampAncestor(hit.object);
        if (lampGroup) {
          if (isMeasurementActiveRef.current || isLampDistanceModeRef.current) return;
          if (isLampQuickEditDisabledRef.current) continue;
          window.dispatchEvent(
            new CustomEvent('lamp-quick-edit', {
              detail: {
                lampId: lampGroup.userData.id,
                screenX: event.clientX,
                screenY: event.clientY,
              },
            }),
          );
          return;
        }

        const { userData } = hit.object;
        if (userData.layerName) {
          window.dispatchEvent(
            new CustomEvent('layer-quick-edit', {
              detail: {
                layerName: userData.layerName,
                originalName: userData.originalName || userData.layerName,
                screenX: event.clientX,
                screenY: event.clientY,
              },
            }),
          );
          return;
        }
      }
    };

    // ── ESC key → cancel active measurement (stay in measurement mode) ──────
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMeasurementActiveRef.current && measurementPointsRef.current.length > 0) {
        measurementActionsRef.current.clearPoints();
        measurementActionsRef.current.clearMeasurementPoints();
      }
    };

    // ── Attach ───────────────────────────────────────────────────────────────
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('click', onClick);
    canvas.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('click', onClick);
      canvas.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKeyDown);
      if (hoverRafId.current) cancelAnimationFrame(hoverRafId.current);
    };
  }, [gl.domElement, camera, raycaster, scene, calculateNDC, getInteractiveObjects]);

  return { calculateNDC, getInteractiveObjects };
}
